import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useAuth } from "@/hooks/use-auth";
import { getDataSource } from "@/lib/data-source";
import { cn } from "@/lib/utils";

export function StaffTodaySummary() {
  const { demoMode } = useDemoMode();
  const { profile } = useAuth();
  const ds = useMemo(
    () => getDataSource(profile?.role === "staff" && demoMode),
    [demoMode, profile?.role],
  );
  const navigate = useNavigate();

  const { data: jobs = [] } = useQuery({
    queryKey: ["staff-jobs", demoMode],
    queryFn: () => ds.listJobs(),
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const twoDaysAgo = new Date(today);
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

  const twentyFourHoursAgo = new Date();
  twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

  const summary = useMemo(() => {
    const dueToday = jobs.filter((j) => {
      if (!j.due_date) return false;
      const dueDate = new Date(j.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate.getTime() === today.getTime();
    });

    const overdue = jobs.filter((j) => {
      if (!j.due_date || j.status === "Completed") return false;
      const dueDate = new Date(j.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate.getTime() < today.getTime();
    });

    const stuckArtworkCheck = jobs.filter((j) => {
      if (j.status !== "Artwork Check") return false;
      const createdDate = new Date(j.created_at);
      return createdDate.getTime() < twoDaysAgo.getTime();
    });

    const stuckGangSheet = jobs.filter((j) => {
      if (j.status !== "Gang Sheet") return false;
      const createdDate = new Date(j.created_at);
      return createdDate.getTime() < twoDaysAgo.getTime();
    });

    const newLast24h = jobs.filter((j) => {
      const createdDate = new Date(j.created_at);
      return createdDate.getTime() >= twentyFourHoursAgo.getTime();
    });

    const printQueue = jobs.filter((j) => {
      return j.status === "Queued for Print" || j.status === "Printing";
    });

    return {
      dueToday: dueToday.length,
      overdue: overdue.length,
      stuckArtworkCheck: stuckArtworkCheck.length,
      stuckGangSheet: stuckGangSheet.length,
      newLast24h: newLast24h.length,
      printQueue: printQueue.length,
    };
  }, [jobs, today, twoDaysAgo, twentyFourHoursAgo]);

  const cards = [
    {
      label: "Due today",
      count: summary.dueToday,
      caption: "Due for completion",
      onClick: () => navigate({ to: "/app/staff/jobs-table", search: { filter: "due-today" } }),
      color: "bg-primary/10 text-primary border-primary/20",
    },
    {
      label: "Overdue",
      count: summary.overdue,
      caption: "Missed deadline",
      onClick: () => navigate({ to: "/app/staff/jobs-table", search: { filter: "overdue" } }),
      color: "bg-red-500/10 text-red-600 border-red-500/20",
    },
    {
      label: "New jobs",
      count: summary.newLast24h,
      caption: "Added in last 24h",
      onClick: () => navigate({ to: "/app/staff/jobs-table", search: { filter: "new-24h" } }),
      color: "bg-green-500/10 text-green-600 border-green-500/20",
    },
    {
      label: "In print queue",
      count: summary.printQueue,
      caption: "Currently printing",
      onClick: () => navigate({ to: "/app/staff/jobs-table", search: { filter: "print-queue" } }),
      color: "bg-purple-500/10 text-purple-600 border-purple-500/20",
    },
    {
      label: "Artwork pending",
      count: summary.stuckArtworkCheck,
      caption: "Awaiting review 2+ days",
      onClick: () => navigate({ to: "/app/staff/jobs-board" }),
      color: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
    },
    {
      label: "Gang sheet pending",
      count: summary.stuckGangSheet,
      caption: "Awaiting layout 2+ days",
      onClick: () => navigate({ to: "/app/staff/jobs-board" }),
      color: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    },
  ];

  return (
    <div className="flex flex-wrap gap-3">
      {cards.map((card) => (
        <button
          key={card.label}
          onClick={card.onClick}
          className={cn(
            "flex-1 min-w-[140px] rounded-2xl border p-3 text-left transition-colors hover:bg-opacity-80 shadow-soft",
            card.color,
          )}
        >
          <div className="text-xs uppercase tracking-wide opacity-70">{card.label}</div>
          <div className="mt-1 text-2xl font-bold">{card.count}</div>
          <div className="mt-1 text-xs opacity-60">{card.caption}</div>
        </button>
      ))}
    </div>
  );
}
